import { RefObject } from 'preact';
import { useState, useEffect, useCallback } from 'preact/hooks';
import { safeBoundingClientRect } from '../utils/domUtils';

interface TooltipPosition {
  top: number;
  left: number;
}

interface UseTooltipProps {
  targetRef: RefObject<HTMLElement | SVGSVGElement>;
  content: string | HTMLElement;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  offset?: number;
  tooltipClass?: string;
  container?: 'body' | RefObject<HTMLElement>;
  showDelay?: number;
  hideDelay?: number;
}

export const useTooltip = ({
  targetRef,
  content,
  placement = 'top',
  offset = 10,
  tooltipClass = '',
  container = 'body',
  showDelay = 0,
  hideDelay = 0,
}: UseTooltipProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>({ top: 0, left: 0 });
  let showTimeout: number;
  let hideTimeout: number;

  const getContainer = useCallback(() => {
    return container === 'body' 
      ? document.body 
      : (container as RefObject<HTMLElement>).current;
  }, [container]);

  const calculatePosition = useCallback(() => {
    if (!targetRef.current) return;

    try {
      // Double-check element still exists before measuring
      if (!targetRef.current || !targetRef.current.isConnected) return;
      
      const targetRect = safeBoundingClientRect(targetRef.current);
      const containerEl = getContainer();
      if (!containerEl) return;
      
      const containerRect = safeBoundingClientRect(containerEl);

      let top = 0;
      let left = 0;

      switch (placement) {
        case 'top':
          top = targetRect.top - containerRect.top + offset;
          left = targetRect.left - containerRect.left + targetRect.width / 2;
          break;
        case 'bottom':
          top = targetRect.bottom - containerRect.top + offset;
          left = targetRect.left - containerRect.left + targetRect.width / 2;
          break;
        case 'left':
          top = targetRect.top - containerRect.top + targetRect.height / 2;
          left = targetRect.left - containerRect.left - offset;
          break;
        case 'right':
          top = targetRect.top - containerRect.top + targetRect.height / 2;
          left = targetRect.right - containerRect.left + offset;
          break;
      }

      setPosition({ top, left });
    } catch (error) {
      // Silently handle DOM measurement errors (element might be removed)
      console.warn('Tooltip position calculation failed:', error);
    }
  }, [targetRef, placement, offset, getContainer]);

  const showTooltip = useCallback(() => {
    clearTimeout(hideTimeout);
    showTimeout = window.setTimeout(() => {
      calculatePosition();
      setIsVisible(true);
    }, showDelay);
  }, [calculatePosition, showDelay]);

  const hideTooltip = useCallback(() => {
    clearTimeout(showTimeout);
    hideTimeout = window.setTimeout(() => {
      setIsVisible(false);
    }, hideDelay);
  }, [hideDelay]);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    target.addEventListener('mouseenter', showTooltip);
    target.addEventListener('mouseleave', hideTooltip);
    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);

    return () => {
      target.removeEventListener('mouseenter', showTooltip);
      target.removeEventListener('mouseleave', hideTooltip);
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, [targetRef, showTooltip, hideTooltip, calculatePosition]);

  return {
    isVisible,
    position,
    content,
    tooltipClass,
    placement
  };
};
